/**
 * Human Renaissance — custom translate widget.
 * Self-driven DOM translator. No Google widget.
 *
 * Why not Google Translate Element?
 *   - Deprecated for new sites since 2019.
 *   - Silently refuses to translate content on localhost/IP hosts,
 *     making preview verification impossible.
 *
 * How this works:
 *   1. Walk the DOM, collect every visible text node that is NOT inside
 *      a .notranslate subtree.
 *   2. Ask a public Lingva Translate endpoint to translate each unique
 *      string from English to the chosen language. Results are cached
 *      in localStorage per language so repeat visits are instant.
 *   3. Swap text-node contents in place. The original English is stored
 *      alongside so "English" restores everything without a reload.
 *
 * Brand name "Human Renaissance", product titles, prices, and logos are
 * protected via the .notranslate class / translate="no" attribute.
 */
/*
 * 2026-09-04 (owner: "click on any vitamin, any fatty acid, anything in the
 * pop-up ... all of that should be translated too. That's a conversion."):
 * two blind spots fixed.
 *   1. <template> contents. The buy box "What it does / Why it matters /
 *      Food comparison" pop-ups are authored inside <template> tags and
 *      cloned into a dialog on tap. A TreeWalker over document.body never
 *      enters template.content, so those strings were never collected and
 *      every pop-up opened in English. Templates are now walked too, so
 *      the clone that opens is already translated.
 *   2. Text that arrives after the first pass. The homepage nutrient cards
 *      copy data-* attributes into a modal with textContent on tap, cart
 *      drawer and section re-renders inject fresh nodes, and any clone of
 *      a template that was translated after it was cloned. A
 *      MutationObserver now watches the page while a language is active
 *      and translates new or changed text nodes from the same cache, with
 *      a guard so it never re-translates text it wrote itself.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'hr_translate_lang';
  // Bump the cache version whenever NOTRANSLATE_SELECTORS or
  // BRAND_KEYWORDS change, so users don't see stale strings from an
  // earlier revision that translated something we now want to preserve.
  var CACHE_PREFIX = 'hr_translate_cache_v4_';
  var DEFAULT_LANG = 'en';

  // Translation backends. Tried in order until one answers.
  //  - Google Translate's unofficial "gtx" endpoint is used by the Chrome
  //    extension and handles bulk requests with generous rate limits. It
  //    returns a nested array like [[["Hola","hello",...]],...] where the
  //    first [0][0][0] element is the translated text.
  //  - Lingva is a public Google Translate proxy — used as a fallback when
  //    the gtx endpoint is unreachable. It is aggressively rate limited
  //    (fails after ~7 rapid requests) so it is a last resort, not primary.
  var BACKENDS = [
    {
      name: 'gtx',
      url: function (lang, text) {
        return 'https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=' + lang + '&dt=t&q=' + encodeURIComponent(text);
      },
      parse: function (j) {
        // j is an array: [[[translated, original, ...], [translated, original, ...]], ...]
        // Google splits long input into sentences — join the first element of
        // each sub-array to reconstruct the full translation.
        if (!j || !j[0]) return null;
        var out = '';
        for (var i = 0; i < j[0].length; i++) {
          var seg = j[0][i];
          if (seg && typeof seg[0] === 'string') out += seg[0];
        }
        return out || null;
      },
    },
    {
      name: 'lingva',
      url: function (lang, text) {
        return 'https://lingva.ml/api/v1/en/' + lang + '/' + encodeURIComponent(text);
      },
      parse: function (j) {
        return j && j.translation ? j.translation : null;
      },
    },
  ];

  // Selectors whose visible text should never be translated.
  //
  // Three categories live here:
  //
  //  1) Brand / identity — the logo, product brand name, attribution
  //     lines. These stay in one language by design.
  //
  //  2) Navigation — the whole header menu (desktop + mobile drawer).
  //     Google Translate renders short nav words ("Shop", "Home",
  //     "Contact Us") with no sentence context, and for some languages
  //     returns alternates that users don't recognize ("Shop" → "Trade"
  //     in several bilingual pivots). Locking the nav keeps routing
  //     predictable in every language. Native speakers on this site
  //     recognize the English words, and the body content still
  //     translates, which is where the real comprehension work is.
  //
  //  3) Glyph boxes / size-constrained chips and badges — elements like
  //     the nutrient card's circular symbol ("C", "Ω7"), its stat
  //     pill ("201 mg"), the research page hero chips and
  //     badges. These have fixed widths / custom typography designed
  //     for the English string length, and break visually when a
  //     longer translation is swapped in.
  //
  // NOTE: we still do NOT lock down .product__title / .card__heading.
  // Those mix a proper-name prefix ("Sea Buckthorn Purée") with
  // descriptive copy ("60 Pouches · 2-Month Supply") — the proper-name
  // portion is handled inline by BRAND_KEYWORDS below, and the rest
  // remains translatable.
  var NOTRANSLATE_SELECTORS = [
    // --- 1) Brand / identity ---
    '.header__heading-logo',
    '.header__heading-link',
    '.h1.header__heading',
    '.hrf__omega7-symbol',
    '.price__container',
    '.price',
    '.price-item',
    '.hr-featured__author',
    '.hr-card__author',

    // --- 2) Navigation (desktop + mobile drawer) ---
    '.header__inline-menu',
    '.header__menu-item',
    '.list-menu--inline',
    '.header__active-menu-item',
    '.menu-drawer',
    '.menu-drawer__menu',
    '.menu-drawer__inner-container',

    // --- 3) Glyph boxes, chips, badges that must preserve layout ---
    // Research page nutrient cards (.rn-nut-card)
    '.rn-nut-card__symbol',
    '.rn-nut-card__stat-value',
    // Research page model hero chips / badge
    '.hr-model__hero-chip',
    '.hr-model__hero-badge',
    '.hr-model__hero-points',
    '.rn-narr__badge',

    // --- 4) Our own translate widget ---
    // The dropdown button, panel, options and their sub-labels are
    // authored in target-language native text ("Español", "हिन्दी",
    // "中文 (简体)"). Walking those through Google Translate breaks
    // the picker for any user who isn't on English.
    '.hr-translate',
    '.hr-translate__panel',
    '.hr-translate__option',
    '.hr-translate__panel-title',
    '.hr-translate__label',
  ];

  // Text nodes containing any of these strings get wrapped in a
  // notranslate span so the surrounding sentence still translates.
  // Order matters: longer phrases are processed first so a later pass
  // (e.g. standalone "Renaissance") never matches text already inside a
  // notranslate wrapper (walkAndWrap skips notranslate subtrees).
  //
  // Rule of thumb: include anything that is a proper name (brand name,
  // company name, botanical species name, ingredient trademark). Leave
  // out ordinary descriptive words.
  var BRAND_KEYWORDS = [
    'Human Renaissance',
    'Sea Buckthorn',
    'Renaissance',
    'Omega-7',
    'Purée',
  ];

  // Track (node -> original English text) so switching back is instant.
  var originals = [];

  /* ------------------------------------------------------------------ */
  /*  Phase 1 — mark brand + product identity untouchable.              */
  /* ------------------------------------------------------------------ */
  function tagNoTranslate() {
    NOTRANSLATE_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.classList.add('notranslate');
        el.setAttribute('translate', 'no');
      });
    });
    BRAND_KEYWORDS.forEach(function (keyword) {
      walkAndWrap(document.body, keyword);
    });
  }

  function walkAndWrap(root, keyword) {
    if (!root) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || node.nodeValue.indexOf(keyword) === -1) return NodeFilter.FILTER_REJECT;
        var p = node.parentNode;
        if (!p || p.nodeType !== 1) return NodeFilter.FILTER_REJECT;
        var tag = p.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') return NodeFilter.FILTER_REJECT;
        if (p.classList && p.classList.contains('notranslate')) return NodeFilter.FILTER_REJECT;
        // An HTML span inside an SVG <text> never renders, so skip SVG entirely.
        if (p.closest && p.closest('svg')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (node) {
      var text = node.nodeValue;
      var parts = text.split(keyword);
      if (parts.length < 2) return;
      var frag = document.createDocumentFragment();
      parts.forEach(function (part, i) {
        if (part) frag.appendChild(document.createTextNode(part));
        if (i < parts.length - 1) {
          var span = document.createElement('span');
          span.className = 'notranslate';
          span.setAttribute('translate', 'no');
          span.textContent = keyword;
          frag.appendChild(span);
        }
      });
      node.parentNode.replaceChild(frag, node);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Phase 2 — collect translatable text nodes.                        */
  /* ------------------------------------------------------------------ */
  function isInsideNoTranslate(node) {
    var cur = node.parentElement;
    while (cur && cur.nodeType === 1) {
      if (cur.classList && cur.classList.contains('notranslate')) return true;
      var tr = cur.getAttribute && cur.getAttribute('translate');
      if (tr === 'no') return true;
      cur = cur.parentElement;
    }
    return false;
  }

  function acceptTextNode(node) {
    var v = node.nodeValue;
    if (!v || !v.trim()) return NodeFilter.FILTER_REJECT;
    var p = node.parentNode;
    if (!p || p.nodeType !== 1) return NodeFilter.FILTER_REJECT;
    var tag = p.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEXTAREA') return NodeFilter.FILTER_REJECT;
    if (isInsideNoTranslate(node)) return NodeFilter.FILTER_REJECT;
    return NodeFilter.FILTER_ACCEPT;
  }

  // Collects translatable text nodes under `root`, INCLUDING the contents
  // of every <template> beneath it (2026-09-04). template.content is a
  // separate DocumentFragment that a TreeWalker over the live tree never
  // enters, which is exactly where the buy-box pop-up copy lives.
  function collectTextNodes(root) {
    var nodes = [];
    if (!root) return nodes;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, { acceptNode: acceptTextNode });
    while (walker.nextNode()) nodes.push(walker.currentNode);
    var templates = [];
    if (root.nodeType === 1 && root.tagName === 'TEMPLATE') templates.push(root);
    if (root.querySelectorAll) {
      root.querySelectorAll('template').forEach(function (t) { templates.push(t); });
    }
    templates.forEach(function (t) {
      if (!t.content) return;
      if (t.closest && t.closest('.notranslate, [translate="no"]')) return;
      BRAND_KEYWORDS.forEach(function (keyword) { walkAndWrap(t.content, keyword); });
      var w = document.createTreeWalker(t.content, NodeFilter.SHOW_TEXT, { acceptNode: acceptTextNode });
      while (w.nextNode()) nodes.push(w.currentNode);
    });
    return nodes;
  }

  /* ------------------------------------------------------------------ */
  /*  Phase 3 — translate via Lingva, with per-language cache.          */
  /* ------------------------------------------------------------------ */
  //
  // Shopify's web-pixel sandbox monkey-patches `window.fetch` and breaks
  // cross-origin requests with "TypeError: Failed to fetch" for URLs the
  // sandbox doesn't whitelist. XMLHttpRequest is not wrapped, so we use
  // it instead for outbound calls to the translation endpoint.
  function xhrGetJson(url) {
    return new Promise(function (resolve, reject) {
      try {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.timeout = 15000;
        xhr.onload = function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch (e) { reject(new Error('parse')); }
          } else {
            reject(new Error('HTTP ' + xhr.status));
          }
        };
        xhr.onerror = function () { reject(new Error('network')); };
        xhr.ontimeout = function () { reject(new Error('timeout')); };
        xhr.send();
      } catch (e) { reject(e); }
    });
  }

  function loadCache(lang) {
    try {
      var raw = localStorage.getItem(CACHE_PREFIX + lang);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  function saveCache(lang, cache) {
    try { localStorage.setItem(CACHE_PREFIX + lang, JSON.stringify(cache)); } catch (e) {}
  }

  function translateOne(text, lang) {
    // Walk the backend list: first one that returns a non-empty string wins.
    return BACKENDS.reduce(function (p, backend) {
      return p.catch(function () {
        return xhrGetJson(backend.url(lang, text)).then(function (j) {
          var t = backend.parse(j);
          if (!t) throw new Error('empty');
          return t;
        });
      });
    }, Promise.reject(new Error('start')));
  }

  // 2026-09-04: BATCHED requests. The single-string endpoint above is
  // throttled per IP after roughly a thousand calls, and one of our pages
  // holds about a thousand strings, so a first-time Spanish visitor was
  // cut off partway through the page and every pop-up opened after that
  // stayed English. Google's dictionary endpoint accepts many q= values in
  // one GET and answers with a plain array in the same order, so a page
  // now costs about 30 requests instead of 1,000. Verified 2026-09-04
  // from the store's own origin: 40 long strings per GET (about 7,000
  // URL characters) succeed, 120 fail with HTTP 400, and the batch
  // endpoint keeps answering while the single one is returning 429.
  var BATCH_MAX_STRINGS = 40;
  var BATCH_MAX_URL_CHARS = 6500;

  function batchUrl(lang, strings) {
    return 'https://translate.googleapis.com/translate_a/t?client=dict-chrome-ex&sl=en&tl=' + lang +
      strings.map(function (q) { return '&q=' + encodeURIComponent(q); }).join('');
  }

  // Resolves to an array of translations aligned with `strings`, or null
  // when the answer cannot be trusted (wrong shape, wrong length, error).
  function translateBatch(strings, lang) {
    return xhrGetJson(batchUrl(lang, strings)).then(function (j) {
      if (!Array.isArray(j) || j.length !== strings.length) return null;
      return j.map(function (item) {
        if (typeof item === 'string') return item;
        if (Array.isArray(item) && typeof item[0] === 'string') return item[0];
        return null;
      });
    }).catch(function () { return null; });
  }

  function makeBatches(pending, lang) {
    var batches = [];
    var cur = [];
    var curLen = batchUrl(lang, []).length;
    pending.forEach(function (q) {
      var add = encodeURIComponent(q).length + 3;
      if (cur.length && (cur.length >= BATCH_MAX_STRINGS || curLen + add > BATCH_MAX_URL_CHARS)) {
        batches.push(cur);
        cur = [];
        curLen = batchUrl(lang, []).length;
      }
      cur.push(q);
      curLen += add;
    });
    if (cur.length) batches.push(cur);
    return batches;
  }

  // Per-string fallback (the original path) for a batch the batch
  // endpoint could not answer. Small concurrency, swallow failures.
  function translateEach(strings, lang, cache, onTranslated) {
    var idx = 0;
    var done = 0;
    var CONCURRENCY = 4;
    return new Promise(function (resolve) {
      function next() {
        if (idx >= strings.length) {
          if (done >= strings.length) resolve();
          return;
        }
        var s = strings[idx++];
        translateOne(s, lang).then(function (t) {
          if (t && t !== s) {
            cache[s] = t;
            if (onTranslated) onTranslated(s, t);
          }
          // Do NOT write identity entries on failure: we'd rather retry
          // the string next visit than permanently mark it untranslatable.
        }).catch(function () {
          // swallow: the string just stays English this run.
        }).then(function () {
          done++;
          next();
        });
      }
      for (var i = 0; i < CONCURRENCY; i++) next();
    });
  }

  // Kicks off translation for `pending` strings, batched, with a small
  // concurrency limit across batches. `onTranslated(source, translated)`
  // fires as each string comes back, so callers can progressively update
  // the DOM instead of waiting for everything to finish.
  function translateMany(pending, lang, cache, onTranslated) {
    if (pending.length === 0) return Promise.resolve();

    var batches = makeBatches(pending, lang);
    var idx = 0;
    var done = 0;
    var CONCURRENCY = 2;

    return new Promise(function (resolve) {
      function next() {
        if (idx >= batches.length) {
          if (done >= batches.length) resolve();
          return;
        }
        var batch = batches[idx++];
        translateBatch(batch, lang).then(function (result) {
          if (!result) return translateEach(batch, lang, cache, onTranslated);
          batch.forEach(function (s, i) {
            var t = result[i];
            if (t && t !== s) {
              cache[s] = t;
              if (onTranslated) onTranslated(s, t);
            }
          });
        }).catch(function () {
          // swallow: this batch stays English this run.
        }).then(function () {
          done++;
          next();
        });
      }
      for (var i = 0; i < CONCURRENCY; i++) next();
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Phase 4 — apply + restore.                                        */
  /* ------------------------------------------------------------------ */
  //
  // Returns a { source -> [entry, entry, ...] } map where each entry is
  // { node, original, leading, trailing, core }. We build it up front
  // so we can apply a translation as soon as it's available — either
  // from the cache (instant) or from a freshly-fetched response
  // (progressive, while other strings are still in flight).
  function buildIndex(nodes) {
    var index = {};
    nodes.forEach(function (n) {
      var original = n.nodeValue;
      var leading = (original.match(/^\s*/) || [''])[0];
      var trailing = (original.match(/\s*$/) || [''])[0];
      var core = original.trim();
      if (!core) return;
      if (!index[core]) index[core] = [];
      index[core].push({
        node: n,
        original: original,
        leading: leading,
        trailing: trailing,
      });
    });
    return index;
  }

  // Every translated string we have ever written into the page for the
  // active language. The observer uses it to recognise text that is
  // already in the target language (a cloned template, a re-rendered
  // section) and leave it alone instead of sending Spanish back to the
  // translator as if it were English.
  var outputs = {};

  function applyOne(source, translated, index) {
    if (!translated || translated === source) return;
    var entries = index[source];
    if (!entries) return;
    outputs[translated.trim()] = true;
    entries.forEach(function (entry) {
      // Idempotent: if a node's current value already matches the
      // translated output, don't re-record or re-write it.
      var target = entry.leading + translated + entry.trailing;
      if (entry.node.nodeValue === target) return;
      originals.push({ node: entry.node, text: entry.original });
      try { entry.node.nodeValue = target; } catch (e) {}
    });
  }

  function restoreEnglish() {
    while (originals.length) {
      var entry = originals.pop();
      try { entry.node.nodeValue = entry.text; } catch (e) {}
    }
    outputs = {};
  }

  /* ------------------------------------------------------------------ */
  /*  Phase 5 — orchestration.                                          */
  /* ------------------------------------------------------------------ */
  // Language currently applied to the page ('en' when none). Read by the
  // mutation observer so it only works while a translation is active.
  var activeLang = DEFAULT_LANG;

  // Translates a batch of text nodes that appeared or changed after the
  // main pass: cache first (instant), then the network for the rest.
  function translateNodes(nodes, lang) {
    if (!nodes.length || !lang || lang === DEFAULT_LANG) return Promise.resolve();
    var fresh = nodes.filter(function (n) {
      var v = n.nodeValue;
      if (!v) return false;
      var core = v.trim();
      if (!core) return false;
      if (outputs[core]) return false; // already in the target language
      return true;
    });
    if (!fresh.length) return Promise.resolve();
    var index = buildIndex(fresh);
    var strings = Object.keys(index);
    var cache = loadCache(lang);
    var pending = [];
    strings.forEach(function (s) {
      if (s in cache) applyOne(s, cache[s], index);
      else pending.push(s);
    });
    if (!pending.length) return Promise.resolve();
    return translateMany(pending, lang, cache, function (source, translated) {
      if (activeLang !== lang) return; // user switched language meanwhile
      applyOne(source, translated, index);
    }).then(function () {
      saveCache(lang, cache);
    }).catch(function () {
      saveCache(lang, cache);
    });
  }

  var observer = null;
  var observerQueue = [];
  var observerTimer = null;

  function flushObserverQueue() {
    observerTimer = null;
    var batch = observerQueue;
    observerQueue = [];
    if (activeLang === DEFAULT_LANG || !batch.length) return;
    var seen = [];
    var nodes = [];
    batch.forEach(function (target) {
      if (!target || seen.indexOf(target) !== -1) return;
      seen.push(target);
      if (target.nodeType === 3) {
        if (acceptTextNode(target) === NodeFilter.FILTER_ACCEPT) nodes.push(target);
      } else if (target.nodeType === 1 || target.nodeType === 11) {
        if (target.closest && target.closest('.notranslate, [translate="no"]')) return;
        BRAND_KEYWORDS.forEach(function (keyword) { walkAndWrap(target, keyword); });
        collectTextNodes(target).forEach(function (n) { nodes.push(n); });
      }
    });
    if (nodes.length) translateNodes(nodes, activeLang);
  }

  function queueForObserver(node) {
    observerQueue.push(node);
    if (!observerTimer) observerTimer = setTimeout(flushObserverQueue, 60);
  }

  function startObserver() {
    if (observer || !window.MutationObserver || !document.body) return;
    observer = new MutationObserver(function (records) {
      if (activeLang === DEFAULT_LANG) return;
      records.forEach(function (r) {
        if (r.type === 'characterData') {
          queueForObserver(r.target);
        } else if (r.type === 'childList') {
          for (var i = 0; i < r.addedNodes.length; i++) queueForObserver(r.addedNodes[i]);
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  function runTranslation(lang) {
    if (!lang || lang === DEFAULT_LANG) {
      activeLang = DEFAULT_LANG;
      restoreEnglish();
      setBusy(false);
      return Promise.resolve();
    }
    activeLang = lang;
    startObserver();
    // Re-run tagging in case sections have been added/re-rendered after
    // the initial boot pass (Shopify dynamic sections, AJAX, etc).
    tagNoTranslate();

    // Flip the document lang immediately — screen readers and the
    // :lang() CSS selector should reflect the user's choice even
    // while background fetches are still running.
    document.documentElement.setAttribute('lang', lang);

    var nodes = collectTextNodes(document.body);
    var index = buildIndex(nodes);
    var strings = Object.keys(index);
    var cache = loadCache(lang);

    // Phase A — apply whatever we already have from the cache. This is
    // synchronous and instant: strings this user has seen on ANY prior
    // page render switch immediately into the target language.
    var pending = [];
    strings.forEach(function (s) {
      if (s in cache) applyOne(s, cache[s], index);
      else pending.push(s);
    });

    if (pending.length === 0) {
      setBusy(false);
      return Promise.resolve();
    }

    // Phase B — fetch everything else and stream results into the DOM
    // as they arrive. Persist the cache at the end so the next page
    // view (including a fresh reload) can hit Phase A instantly.
    setBusy(true);
    return translateMany(pending, lang, cache, function (source, translated) {
      applyOne(source, translated, index);
    }).then(function () {
      saveCache(lang, cache);
      setBusy(false);
    }).catch(function (e) {
      console.warn('[hr-translate] failed', e);
      saveCache(lang, cache);
      setBusy(false);
    });
  }

  function setBusy(flag) {
    var btn = document.querySelector('[data-hr-translate-toggle]');
    if (!btn) return;
    if (flag) btn.setAttribute('data-busy', 'true');
    else btn.removeAttribute('data-busy');
  }

  function applyLanguage(lang) {
    if (!lang) return;
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) {}
    if (lang === DEFAULT_LANG) {
      activeLang = DEFAULT_LANG;
      restoreEnglish();
      document.documentElement.setAttribute('lang', 'en');
    } else {
      // If a previous target language was already applied, revert to
      // English first so the new language's cache maps cleanly onto
      // the original source strings.
      restoreEnglish();
      runTranslation(lang);
    }
  }

  function getSavedLang() {
    try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG; } catch (e) { return DEFAULT_LANG; }
  }

  /* ------------------------------------------------------------------ */
  /*  Phase 6 — dropdown UI wiring.                                     */
  /* ------------------------------------------------------------------ */
  function initButton() {
    var root = document.querySelector('[data-hr-translate]');
    if (!root) return;

    var btn = root.querySelector('[data-hr-translate-toggle]');
    var panel = root.querySelector('[data-hr-translate-panel]');
    if (!btn || !panel) return;

    var current = getSavedLang();
    markSelected(panel, current);

    // Remember the panel's original home so we can restore it after portaling.
    // On mobile, we portal the panel to document.body so position:fixed is not
    // trapped by any ancestor transform/filter on the sticky header, which was
    // clipping the dropdown and pinning it to the top of the viewport.
    var panelHome = panel.parentNode;
    var panelPortaled = false;

    function isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    function portalPanel() {
      if (panelPortaled) return;
      if (!isMobile()) return;
      document.body.appendChild(panel);
      panel.setAttribute('data-portaled', 'true');
      panelPortaled = true;
    }

    function restorePanel() {
      if (!panelPortaled) return;
      panel.removeAttribute('data-portaled');
      if (panelHome) panelHome.appendChild(panel);
      panelPortaled = false;
    }

    function open() {
      portalPanel();
      panel.hidden = false;
      requestAnimationFrame(function () { panel.setAttribute('data-open', 'true'); });
      btn.setAttribute('aria-expanded', 'true');
    }

    function close() {
      panel.removeAttribute('data-open');
      btn.setAttribute('aria-expanded', 'false');
      setTimeout(function () {
        if (btn.getAttribute('aria-expanded') === 'false') {
          panel.hidden = true;
          restorePanel();
        }
      }, 200);
    }

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (btn.getAttribute('aria-expanded') === 'true') close();
      else open();
    });

    panel.addEventListener('click', function (e) {
      var opt = e.target.closest('.hr-translate__option');
      if (!opt) return;
      var lang = opt.getAttribute('data-lang');
      markSelected(panel, lang);
      applyLanguage(lang);
      close();
    });

    document.addEventListener('click', function (e) {
      // When portaled the panel is no longer a descendant of root, so we
      // have to check the panel explicitly for the outside-click test.
      if (root.contains(e.target)) return;
      if (panel.contains(e.target)) return;
      close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  function markSelected(panel, lang) {
    panel.querySelectorAll('.hr-translate__option').forEach(function (o) {
      if (o.getAttribute('data-lang') === lang) o.setAttribute('aria-selected', 'true');
      else o.removeAttribute('aria-selected');
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Boot                                                              */
  /* ------------------------------------------------------------------ */
  function boot() {
    tagNoTranslate();
    initButton();

    var saved = getSavedLang();
    if (saved && saved !== DEFAULT_LANG) {
      // Re-apply on every page view using cached strings (instant) and
      // fetch any new ones (e.g. on a new page the user hasn't visited).
      runTranslation(saved);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
